import { IBookingSlotsFilterBody, IUserBookingsListData } from "@/api_framework/api_modals/Bookings";
import { RouteKeys } from "@/api_framework/api_modals/Routers";
import { IBookingCancelationModalState } from "@/app/components/routes/modals/Cancelation";
import { IBookingMissingDetailsEditModalState } from "@/app/components/routes/modals/MissingDetailsBooking";
import { useIsBookingChargableAPI } from "@/app/hooks/api_hooks/bookings/useIsBookingChargableAPI";
import { useUserBookingsListAPI } from "@/app/hooks/api_hooks/bookings/useUserBookingsListApi";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import usePagination from "@/app/hooks/usePagination";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useUtils } from "@/app/hooks/useUtils";
import { ROUTES } from "@/app/routes";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { setModalStateData } from "@/app_redux/reducers/modal_state_reducer";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import {
  ISlotsState,
  RESET_SLOTS_SLICE,
  setRescheduleBookingIdForMissingInfo,
  setRescheduleFlag,
  setRescheduledBooking,
  setSlotBookingFilters,
  setSlotsList,
} from "@/app_redux/reducers/slices/auth/slots_slice";
import clsx from "clsx";
import { Menu, Transition } from "@headlessui/react";

import RBACFlag from "@/app/components/core/RBACFlag";
import { IEditBookingCommentModalState } from "@/app/components/routes/modals/EditBookingComment";
import { IBookingRefundModalState } from "@/app/components/routes/modals/RefundInitiate";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import ClipboardComponent from "@/app/components/ui/Clipboard";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import BookingDateTime from "@/app/components/ui/Reusable/BookingDateTime";
import BookingStatus from "@/app/components/ui/Reusable/BookingStatus";
import PetName from "@/app/components/ui/Reusable/PetName";
import { useSlideoverActions } from "@/app/hooks/ui_hooks/useSlideoverActions";
import { ClockIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { debounce, startCase } from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import { BiLoaderAlt } from "react-icons/bi";
import { FiMoreVertical, FiRefreshCw } from "react-icons/fi";
import { useNavigate } from "react-router";
import { Tooltip } from "react-tooltip";
import SendWhatsappScriptimport from "@components/routes/auth_routes/SlotsScreen/SendWhatsappScript";
import AppointmentStatusButton from "../AppointmentStatusButton";
import { toDiagnosticsReplacement } from "@/app/utils/bookingServiceSelection";
import Type3 from "@/app/components/ui/Skeletons/Type3";

const LIMIT = 10;
const tableHeader = [
  {
    item: "SN",
  },
  {
    item: "Date & Time",
  },
  {
    item: "Service",
  },
  {
    item: "Provider",
  },
  {
    item: "Pet",
  },
  {
    item: "Clinic",
  },
  {
    item: "Appt Status",
  },
  {
    item: "Comments",
  },
  {
    item: "Payment Details",
  },
  {
    item: "Booking History",
  },
  {
    item: "Invoice",
  },
  {
    item: "Created By",
  },
  {
    item: "Action",
  },
];

const AppointmentTableComponent = () => {
  /* Instantiations Starts */
  const stringConstants = useStringConstants();
  const { to12HrFormat, veticDateFormat, getDisplayStaffName, truncateString } = useUtils();
  const { NO_APPOINTMENTS } = useImageConstants();
  const { rescheduleAndCancelAllowedCBLFor, bookAgainAllowedCBLFor } = useArrayConstants();
  const { setVisible: setCancelModalVisible, setModalData: setModalData } = useModalActions("APPOINTMENT_CANCELATION");
  const { setVisible: setReschedule, setModalData: setRescheduleData } = useModalActions("APPOINTMENT_RESCHEDULE");
  const { setVisible: setRefund, setModalData: setRefundData } = useModalActions("APPOINTMENT_REFUND");
  const { setVisible } = useModalActions("CUSTOMER_PROFILE_APPOINTMENTS_TABLE_COMMENTS_VIEW_MORE");
  const { setVisible: setEditVisible, setModalData: setEditModalData } = useModalActions(
    "CUSTOMER_PROFILE_APPOINTMENTS_TABLE_COMMENTS_EDIT",
  );
  const { setVisible: setPaymentVisible, setModalData: setPaymentModalData } =
    useModalActions("BOOKINGS_PAYMENT_STATUS");

  const { setVisible: viewBookingDetails, setSlideoverData: BookingDetailsData } =
    useSlideoverActions("BOOKING_DETAILS_VIEW");

  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const isCustomerBookingFetchInProgress = useLoader("isCustomerBookingsFetchInProgress");
  const dispatch = useAppDispatch();
  // RBAC: PERSONA_DISCOUNT_COMMENTS_VIEW — controls visibility of the persona discount callout
  const canViewPersonaDiscountComments = useFeatureAccess("PERSONA_DISCOUNT_COMMENTS_VIEW");
  // Front desk overbooking users cannot reschedule
  const isFrontDeskOverbooking = useFeatureAccess("FRONT_DESK_OVERBOOKING");
  const { setModalData: setMissingDetailsData, setVisible: setMissingDetailsModalVisible } = useModalActions(
    "BOOKING_MISSING_DETAILS_EDIT_MODAL",
  );

  /* Instantiations Ends Here */

  const [open, setOpen] = useState<boolean>(false);
  const [bookingId, setBookingId] = useState<string>(null);

  /* API Starts Here */
  const fetchCustomerBookingsAPI = useUserBookingsListAPI();
  const { execute: fetchBookingStatus } = useIsBookingChargableAPI();
  const { execute: logClick } = useClickLogAPI();
  /* API Ends Here */

  /* Click-log analytics */
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);
  const mappedClinicId = useAppSelector((state) => state.root.UIStates.mappedClinicId);

  /* Redux State Hooks */
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.SLOTS.id);
  const { routeState: customerProfileRouteState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerDetails, petDetails, customerId, petId, appointmentFilter } =
    customerProfileRouteState.state as ICustomerProfileState;
  const { customerBookings } = routeState.state as ISlotsState;

  const { setSlideoverData, setVisible: setInvoiceVisibale } = useSlideoverActions("CUSTOMER_PROFILE_DISCOUNT_DETAILS");

  const pagination = usePagination(customerBookings?.count, LIMIT);
  const debouncedNextPage = useMemo(
    () =>
      debounce(() => {
        pagination.next();
      }, 350),
    [pagination.next],
  );

  /* Redux States Ends Here */

  const missingKeysInCustomer = () => {
    const mandatoryKeys = ["name", "mobile"];
    const missingDetails = Object.keys(customerDetails).filter((cdKey) => {
      return mandatoryKeys.includes(cdKey) && customerDetails[cdKey] === null;
    });
    // if (customerAddresses?.length === 0) {
    //   missingDetails.push("At Least One Address");
    // }
    if (!petId) {
      missingDetails.push("Add at least 1 pet");
    }
    return missingDetails;
  };

  // A future-dated DOB yields a negative age, which is invalid for booking.
  // (A null/empty DOB is already caught by the mandatory-key checks below.)
  const isDobNegative = (dob?: string | null): boolean => {
    if (!dob) return false;
    // Parse "YYYY-MM-DD" as a LOCAL date (not UTC via new Date(str)) so a pet born
    // today isn't wrongly read as future-dated in +offset timezones like IST.
    const [year, month, day] = dob.split("-").map(Number);
    if (!year || !month || !day) return false;
    const dobDate = new Date(year, month - 1, day);
    if (isNaN(dobDate.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dobDate.getTime() > today.getTime();
  };

  const missingKeysInPets = () => {
    if (!petDetails) return [];
    const mandatoryKeys = ["pet_breed_id", "pet_species_type_id", "dob", "name", "gender", "weight"];

    // Only the mandatory fields that are actually null/empty (or a future-dated DOB)
    // are "missing" — evaluate each key on its own value, never a form-wide condition.
    const missingDetails = Object.keys(petDetails)
      .filter((key) => {
        if (!mandatoryKeys.includes(key)) return false;
        const value = petDetails[key];
        if (value === null || value === "") return true;
        if (key === "dob" && isDobNegative(petDetails?.dob)) return true;
        return false;
      })
      .map((key) => startCase(key.split("_")?.join(" ")));

    // An inactive/deceased pet can't be booked, so surface Status as its own item
    // instead of (incorrectly) flagging every field as missing.
    const isStatusActive = petDetails?.status?.toLowerCase() === "active";
    if (!isStatusActive) {
      missingDetails.push("Status");
    }

    return missingDetails;
  };

  const isThereAnyMissingDetails = () => {
    return {
      pet: {
        count: missingKeysInPets()?.length,
        keys: missingKeysInPets(),
      },
      customer: {
        count: missingKeysInCustomer()?.length,
        keys: missingKeysInCustomer(),
      },
    };
  };

  useEffect(() => {
    if (!appointmentFilter) return;
    if (!customerId || !petId) return;

    // Reset pagination whenever profile context/filter changes.
    pagination.jump(1);
    fetchCustomerBookingsAPI(customerId, petId, {
      append: false,
      filter_by: appointmentFilter?.toLowerCase(),
      limit: LIMIT,
      page: 1,
    });
  }, [customerId, petId, appointmentFilter]);

  useEffect(() => {
    // Fetch subsequent pages only; page 1 is fetched by the context/filter effect above.
    if (!customerId || !petId || !appointmentFilter) return;
    if (pagination.currentPage <= 1 || pagination.currentPage > pagination.maxPage) return;

    fetchCustomerBookingsAPI(customerId, petId, {
      limit: LIMIT,
      page: pagination.currentPage,
      append: true,
      filter_by: appointmentFilter?.toLowerCase(),
    });
  }, [pagination.currentPage, pagination.maxPage, customerId, petId, appointmentFilter, fetchCustomerBookingsAPI]);

  useEffect(() => {
    return () => {
      debouncedNextPage.cancel();
    };
  }, [debouncedNextPage]);

  const handleCancellation = (bookingDetails: IUserBookingsListData) => {
    setModalData({
      customerProfileBookingDetails: bookingDetails,
      from: "CUSTOMER_PROFILE",
    } as IBookingCancelationModalState);
    setCancelModalVisible();
  };
  const handelRefund = (bookingDetails: IUserBookingsListData) => {
    setRefundData({
      booking_id: bookingDetails?.booking_id,
    } as IBookingRefundModalState);
    setRefund();
  };

  const handleBookAppointment = () => {
    const logBody: IClickLogBody = {
      event_type: "BOOK_APPOINTMENT_CLICK",
      scr_id: "CUSTOMER_PROFILE",
      cta_text: "Book Appointment",
      vms_user_id: vmsUserId,
      clinic_id: mappedClinicId,
      client_id: customerId,
      pet_id: petId,
      timestamp: Date.now(),
    };
    logClick(logBody);

    /* Reset Slots slice to avoid conflicting of data */
    dispatch(RESET_SLOTS_SLICE());
    const missingDetails = isThereAnyMissingDetails();
    if (missingDetails?.pet?.count > 0 || missingDetails?.customer?.count > 0) {
      setMissingDetailsData({
        customerId: customerId,
        petId: petId,
        missingDetails: isThereAnyMissingDetails(),
      } as IBookingMissingDetailsEditModalState);
      setMissingDetailsModalVisible();
    } else {
      navigate(`${getRouteKey("AVAILABLE_SLOTS", "url")}/${customerId}/${petId}`);
    }
  };

  const reconstructBookingFilters = (
    bookingDetails: IUserBookingsListData,
    rescheduled = false,
  ): IBookingSlotsFilterBody => {
    // A reschedule keeps the booking's own service; booking again moves a legacy diagnostic one to Diagnostics.
    const { serviceId, subServiceId } = rescheduled
      ? { serviceId: bookingDetails?.service_id, subServiceId: bookingDetails?.subservice_id }
      : toDiagnosticsReplacement(bookingDetails?.service_id, bookingDetails?.subservice_id);
    return {
      booking_date: rescheduled ? bookingDetails?.booking_start_date : format(new Date(Date.now()), "yyyy-MM-dd"),
      clinic_ids: [],
      location_details: {
        latitude: "",
        longitude: "",
      },
      pet_species_id: petDetails?.pet_species_type_id,
      service_id: serviceId,
      staff_ids: [],
      sub_service_id: subServiceId,
      city_id: "",
      slot_status: stringConstants?.slotStatusAvailable,
    };
  };

  const handleBookAgain = (bookingDetails: IUserBookingsListData) => {
    // Make the slots list empty.
    const missingDetails = isThereAnyMissingDetails();
    if (missingDetails?.pet?.count > 0 || missingDetails?.customer?.count > 0) {
      setMissingDetailsModalVisible();
    } else {
      const filtersBody = reconstructBookingFilters(bookingDetails);
      dispatch(
        setSlotsList({
          data: [],
        }),
      );
      dispatch(
        setSlotBookingFilters({
          filters: filtersBody,
        }),
      );
      navigate(`${getRouteKey("AVAILABLE_SLOTS", "url")}/${customerId}/${petId}`);
    }
  };

  const handleBookingReschedule = (bookingDetails: IUserBookingsListData) => {
    /* slots list making it empty */
    const missingDetails = isThereAnyMissingDetails();
    if (missingDetails?.pet?.count > 0 || missingDetails?.customer?.count > 0) {
      dispatch(setRescheduleFlag(true));
      dispatch(setRescheduleBookingIdForMissingInfo(bookingDetails?.booking_id));
      setMissingDetailsModalVisible();
    } else {
      const filtersBody = reconstructBookingFilters(bookingDetails, true);
      dispatch(
        setSlotsList({
          data: [],
        }),
      );

      dispatch(
        setSlotBookingFilters({
          filters: filtersBody,
        }),
      );
      /* setting the bookings data for rescheduling */
      dispatch(
        setRescheduledBooking({
          data: bookingDetails,
        }),
      );

      /* Set Reschedule Flag */
      dispatch(setRescheduleFlag(true));
      if (bookingDetails?.payment_details?.payment_status) {
        setReschedule();
        setRescheduleData({
          userId: customerId,
          petId: petId,
          bookingId: bookingDetails?.booking_id,
          price: bookingDetails?.payment_details?.amount,
          isRefunded: bookingDetails?.payment_details?.is_refundable,
        });
      } else {
        navigate(
          `${getRouteKey(
            "AVAILABLE_SLOTS",
            "url",
          )}/${customerId}/${petId}?type=reschedule&rbi=${bookingDetails?.booking_id}`,
        );
      }
    }
  };

  const handleCommentView = (data: IUserBookingsListData) => {
    dispatch(
      setModalStateData({
        key: "CUSTOMER_PROFILE_APPOINTMENTS_TABLE_COMMENTS_VIEW_MORE",
        data: {
          appointmentNotes: data?.appointment_note,
          personaDiscountComments: data?.persona_discount_comments ?? [],
        },
      }),
    );
    setVisible();
  };
  const handleCommentEdit = (data: IUserBookingsListData) => {
    setEditModalData({
      bookingDetails: data,
    } as IEditBookingCommentModalState);
    setEditVisible();
  };

  const handleRefreshPaymentStatus = async (selectedBookingId: string) => {
    if (!selectedBookingId) return;
    const response = await fetchBookingStatus(selectedBookingId).catch(() => null);
    if (!response?.status) return;
    if (customerId && petId) {
      fetchCustomerBookingsAPI(customerId, petId);
    }
  };
  
  const isPetFetchInProgress = useLoader("isPetListFetchInProgress");
  const isCustomerDetailsFetchInProgress = useLoader("isCustomerDetailsFetchInProgress");
  const totalBookingsCount = customerBookings?.count ?? 0;
  const loadedBookingsCount = customerBookings?.data?.length ?? 0;
  const hasMoreBookings = loadedBookingsCount < totalBookingsCount;
  type ActionItem = {
    key: string;
    label: string;
    onClick: () => void;
    rbacActionId?: RouteKeys;
    disabled?: boolean;
  };

  return (
    <div className="pl-2">
      <div className="flex justify-between pt-1 items-center">
        <div className="flex ">
          <h1 className="text-sm  leading-6 font-semibold ">Appointment Details</h1>
        </div>

        <div data-tooltip-id="appointment_button" data-tooltip-content="Pet is deceased cannot book the appointment.">
          {isCustomerDetailsFetchInProgress || isPetFetchInProgress ? (
            <Type3 />
          ) : (
            petDetails && (
              <RBACFlag actionId="AVAILABLE_SLOTS">
                <SolidButton
                  disabled={petDetails?.status?.toLowerCase() === "deceased"}
                  onButtonClick={() => handleBookAppointment()}
                  size="2XL"
                  className="!h-8 !px-4 !py-2 !rounded-lg !bg-black !border-black hover:!bg-black focus:!bg-black active:!bg-black !text-white !font-medium !text-[12px] !leading-none whitespace-nowrap"
                >
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <span
                      className={clsx(
                        petDetails?.status?.toLowerCase() === "deceased"
                          ? "cursor-not-allowed bg-gray-400"
                          : "cursor-pointer",
                      )}
                    >

                    </span>{" "}
                    <span className="whitespace-nowrap text-xs">Book Appointment</span>
                  </span>
                </SolidButton>
              </RBACFlag>
            )
          )}
          {petDetails?.status?.toLowerCase() === "deceased" && (
            <Tooltip
              id="appointment_button"
              style={{
                fontSize: "10px",
              }}
            />
          )}
        </div>
      </div>
      <hr className="mt-2" />
      <div className="my-2 2xl:my-3">
        <AppointmentStatusButton />
      </div>

      {/* {customerDetails && customerId && ( */}
      <div className="mt-1 flow-root">
        {isCustomerBookingFetchInProgress && (
          <div className="absolute bottom-0 left-0 z-10 rounded-md w-full p-4 bg-blue-50">
            <div className="w-full flex justify-center items-center">
              <span className="w-4 h-4 flex justify-center items-center gap-x-2 flex-col animate-spin">
                <span className="text-lg text-blue-600">
                  <BiLoaderAlt />
                </span>
              </span>
              <span className="ml-2 text-blue-700 text-xs">Fetching past bookings ...</span>
            </div>
          </div>
        )}
        <div
          className="outer-container relative rounded-xl bg-[#F9FAFB]"
        >
          <div
            className="scroll-inner !h-auto max-h-[450px] 2xl:max-h-[550px]"
            onScroll={(e: any) => {
              const element = e.target;
              if (element.scrollTop + element.offsetHeight >= element.scrollHeight - 100) {
                if (hasMoreBookings && pagination.currentPage < pagination.maxPage) {
                  if (isCustomerBookingFetchInProgress) return;
                  debouncedNextPage();
                }
              }
            }}
          >
            {customerBookings?.data?.length > 0 ? (
              <>
                <div className="w-full">
                  <div className="inline-block min-w-full py-1 align-middle">
                    <div className="bg-white rounded-lg">
                      <table className="w-full min-w-[1200px] sm:min-w-[1320px] md:min-w-[1480px] xl:min-w-[1600px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                          <tr>
                            {tableHeader?.map((key, index) => (
                              <th
                                key={index}
                                scope="col"
                                className={`py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize ${["SN", "Date & Time", "Service", "Provider"].includes(key.item) ? "px-2" : "px-3"} ${key.item === "SN" ? "min-w-[44px]" : ""
                                  } ${key.item === "Date & Time" ? "min-w-[105px]" : ""
                                  } ${key.item === "Service" ? "min-w-[110px]" : ""
                                  } ${key.item === "Provider" ? "min-w-[140px]" : ""
                                  } ${key.item === "Clinic" ? "min-w-[170px]" : ""
                                  } ${key.item === "Action" ? "min-w-[185px] sm:sticky sm:right-0 sm:z-30 bg-[#F5F5F5]" : ""}`}
                              >
                                {key.item}
                              </th>
                            ))}
                          </tr>
                        </thead>

                        <tbody className="overflow-hidden rounded-xl w-full">
                          {customerBookings?.data?.map((cb, id) => {
                            const isFirst = id === 0;
                            const isLast = id === customerBookings?.data?.length - 1;
                            const isSingle = customerBookings?.data?.length === 1;
                            const providerName = getDisplayStaffName(cb) || "NA";
                            const clinicName = cb?.clinic_details?.display_name ?? "Not Available";

                            return (
                              <tr
                                key={id}
                                className={clsx(
                                  "group bg-white transition",
                                  isSingle && "rounded-xl",
                                  isFirst && !isSingle && "rounded-t-xl",
                                  isLast && !isSingle && "rounded-b-xl",
                                )}
                              >
                                <td
                                  className={clsx(
                                    "px-2 py-3 whitespace-nowrap text-xs font-normal min-w-[44px]",
                                    isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                    isFirst && !isSingle && "rounded-tl-2xl",
                                    isLast && !isSingle && "rounded-bl-2xl",
                                  )}
                                >
                                  <div className="flex flex-col">{id + 1}</div>
                                </td>
                                <td className="px-2 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F] min-w-[105px]">
                                  <div className="flex flex-col">
                                    <span>{veticDateFormat(cb?.booking_start_date)}</span>
                                    <BookingDateTime
                                      className="text-xs flex justify-start break-keep items-center"
                                      startDateTime={cb?.booking_start_date_time}
                                    />
                                  </div>
                                </td>
                                <td className="px-2 py-3 whitespace-nowrap text-xs font-normal min-w-[110px]">
                                  <div className="w-full h-full flex justify-center items-start flex-col overflow-hidden">
                                    <span className="block w-full truncate">{cb?.service_details?.service_type}</span>
                                    <span className="mt-1 block w-full truncate text-[#808DA0]">{cb?.subservice_details?.name}</span>
                                  </div>
                                </td>
                                <td className="px-2 py-3 whitespace-nowrap text-xs font-normal min-w-[140px]">
                                  <div className="flex min-w-0 flex-col overflow-hidden">
                                    <span
                                      className="block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                                      title={providerName}
                                    >
                                      {truncateString(providerName, 16)}
                                    </span>
                                  </div>
                                </td>
                                <td className="p-3 whitespace-nowrap text-xs font-normal">
                                  <div className="flex flex-col">
                                    <PetName petName={cb?.pet_details?.name} petStatus={""} />
                                  </div>
                                </td>
                                <td className="p-3 whitespace-nowrap text-xs font-normal min-w-[170px]">
                                  <div className="flex flex-col overflow-hidden">
                                    <div className="flex items-center gap-2 group max-w-full">
                                      <ClipboardComponent
                                        className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                        copyId={`clinic-name-${cb?.booking_id}`}
                                        text={clinicName}
                                        tooltipText="Copy clinic name"
                                      />
                                      <span className="block w-full truncate" title={clinicName}>
                                        {truncateString(clinicName, 20)}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-3 whitespace-nowrap text-xs font-normal">
                                  <div className="flex flex-col">
                                    <BookingStatus bookingStatus={cb?.booking_status} />
                                  </div>
                                </td>
                                <td className="p-3 whitespace-nowrap text-xs font-normal">
                                  <div className="flex flex-col">
                                    {(cb?.appointment_note !== null && cb?.appointment_note !== "<ol><li></li></ol>") ||
                                    (canViewPersonaDiscountComments && cb?.persona_discount_comments?.length > 0) ? (
                                      <div className="flex flex-col gap-1">
                                        {cb?.appointment_note !== null &&
                                          cb?.appointment_note !== "<ol><li></li></ol>" &&
                                          (cb?.booking_status === "Upcoming" ||
                                            cb?.booking_status === "Waiting" ||
                                            cb?.booking_status === "Attending") && (
                                            <RBACFlag actionId="EDIT_BOOKING_COMMENT_PERMISION">

                                              <a
                                                href="#"
                                                className="underline text-xs text-black"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  handleCommentEdit(cb);
                                                }}
                                              >
                                                Edit Comments
                                              </a>
                                            </RBACFlag>
                                          )}
                                        <a
                                          href="#"
                                          className="underline text-xs text-black"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleCommentView(cb);
                                          }}
                                        >
                                          View Comments
                                        </a>
                                      </div>
                                    ) : (
                                      <p className="text-xs">NA</p>
                                    )}
                                  </div>
                                </td>
                                <td className="p-3 whitespace-nowrap text-xs font-normal">
                                  {cb?.booking_status?.toLowerCase() === "on hold" ? (
                                    <div className="flex items-center gap-1">
                                      <ClipboardComponent
                                        className=""
                                        copyId={cb?.payment_details?.payment_link}
                                        text={cb?.payment_details?.payment_link}
                                        tooltipText="Copy Payment Link"
                                      />
                                      <span className="text-xs">Payment Link</span>
                                    </div>
                                  ) : cb?.booking_mode?.toLowerCase() === "paid" ? (
                                    <a
                                      onClick={() => {
                                        setPaymentModalData({
                                          bookingDetails: cb,
                                        });
                                        setPaymentVisible();
                                      }}
                                      href="#"
                                      className="underline text-xs text-black"
                                    >
                                      Show payment history
                                    </a>
                                  ) : (
                                    "NA"
                                  )}
                                </td>
                                <td className="p-3 whitespace-nowrap text-xs font-normal">
                                  <div className="flex flex-col">
                                    <a
                                      href="#"
                                      className="underline text-xs text-black"
                                      onClick={() => {
                                        BookingDetailsData({ booking_id: cb?.booking_id });
                                        viewBookingDetails();
                                      }}
                                    >
                                      View Booking history
                                    </a>
                                  </div>
                                </td>
                                <td className="p-3 whitespace-nowrap text-xs font-normal">
                                  <div className="flex flex-col">
                                    <p className="mb-1">{cb?.booking_discount_details?.discount}</p>
                                    {cb?.order_summary_url ? (
                                      <a
                                        href={cb?.order_summary_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline text-xs text-black"
                                      >
                                        View Summary
                                      </a>
                                    ) : (
                                      <p className="text-xs">NA</p>
                                    )}
                                  </div>
                                </td>
                                <td className="p-3 whitespace-nowrap text-xs font-normal">
                                  <div className="flex flex-col">
                                    <span className="text-blue-600">{cb?.created_by}</span>
                                    <span className="flex justify-start items-center gap-x-2">
                                      <span>
                                        <ClockIcon className="w-3 h-3" />
                                      </span>{" "}
                                      <span>Last Edit : {veticDateFormat(cb.updated_at)}</span>
                                    </span>
                                  </div>
                                </td>

                                <td
                                  className={clsx(
                                    "p-2 whitespace-normal text-xs font-normal bg-gray-100 align-middle min-w-[185px] sm:sticky sm:right-0 sm:z-20",
                                  )}
                                >
                                  {(() => {
                                    const actionItems: ActionItem[] = [];

                                    if (rescheduleAndCancelAllowedCBLFor?.includes(cb?.booking_status?.toLowerCase())) {
                                      if (
                                        !isFrontDeskOverbooking &&
                                        (cb?.booking_status === "Upcoming" ||
                                          cb?.booking_status === "No Show" ||
                                          cb?.booking_status === "Waiting")
                                      ) {
                                        actionItems.push({
                                          key: "reschedule",
                                          label: "Reschedule",
                                          onClick: () => handleBookingReschedule(cb),
                                          rbacActionId: "AVAILABLE_SLOTS_RESCHEDULE",
                                        });
                                      }
                                      actionItems.push({
                                        key: "cancel",
                                        label: "Cancel",
                                        onClick: () => handleCancellation(cb),
                                        disabled: petDetails?.status?.toLowerCase() === "deceased",
                                        rbacActionId: "AVAILABLE_SLOTS_CANCELATION",
                                      });
                                    }

                                    if (bookAgainAllowedCBLFor?.includes(cb?.booking_status?.toLowerCase())) {
                                      actionItems.push({
                                        key: "book-again",
                                        label: "Book Again",
                                        onClick: () => handleBookAgain(cb),
                                      });
                                      if (cb?.payment_details?.is_refundable_manually) {
                                        actionItems.push({
                                          key: "refund",
                                          label: "Initiate Refund",
                                          onClick: () => handelRefund(cb),
                                          rbacActionId: "ALLOW_BOOKING_MANUAL_REFUND",
                                        });
                                      }
                                    }

                                    if (cb?.booking_status?.toLowerCase() === "on hold") {
                                      actionItems.push({
                                        key: "send-whatsapp",
                                        label: "Send Whatsapp",
                                        onClick: () => {
                                          setBookingId(cb?.booking_id);
                                          setOpen(true);
                                        },
                                      });
                                      actionItems.push({
                                        key: "refresh-payment-status",
                                        label: "Payment Status",
                                        onClick: () => handleRefreshPaymentStatus(cb?.booking_id),
                                      });
                                      actionItems.push({
                                        key: "on-hold-cancel",
                                        label: "Cancel",
                                        onClick: () => handleCancellation(cb),
                                      });
                                    }

                                    if (actionItems.length === 0) return <span className="text-xs text-gray-500"></span>;
                                    const primaryAction = actionItems[0];
                                    const menuActions = actionItems.slice(1);

                                    const renderActionButton = (action: ActionItem, menu = false, wrapRbac = true, active = false) => {
                                      const actionLabel = action.key === "send-whatsapp" && !menu ? "Whatsapp" : action.label;
                                      const isCancelAction = action.key === "cancel" || action.key === "on-hold-cancel";
                                      const buttonNode = (
                                        <button
                                          onClick={action.onClick}
                                          disabled={action.disabled}
                                          className={clsx(
                                            menu
                                              ? clsx(
                                                "w-full text-left px-4 py-2 text-sm transition-colors",
                                                isCancelAction
                                                  ? active
                                                    ? "bg-[#FFF1F4] text-[#E11D48]"
                                                    : "text-[#E11D48]"
                                                  : active
                                                    ? "bg-gray-50 text-gray-600"
                                                    : "text-gray-600",
                                              )
                                              : clsx(
                                                "w-[120px] py-2 rounded-lg text-[12px] font-medium disabled:opacity-50 disabled:cursor-not-allowed",
                                                isCancelAction
                                                  ? "bg-[#FFF1F4] text-[#E11D48] border border-[#F9C7D4] hover:bg-[#FFE7EE]"
                                                  : "bg-black text-white hover:opacity-90",
                                              ),
                                          )}
                                        >
                                          {action.key === "refresh-payment-status" ? (
                                            <span className="inline-flex items-center gap-2">
                                              <FiRefreshCw className="w-3.5 h-3.5" />
                                              <span>Payment Status</span>
                                            </span>
                                          ) : (
                                            actionLabel
                                          )}
                                        </button>
                                      );
                                      if (wrapRbac && action.rbacActionId) {
                                        return (
                                          <RBACFlag actionId={action.rbacActionId} key={action.key}>
                                            {buttonNode}
                                          </RBACFlag>
                                        );
                                      }
                                      return <span key={action.key}>{buttonNode}</span>;
                                    };

                                    return (
                                      <div className="flex items-center gap-2 relative">
                                        {renderActionButton(primaryAction)}
                                        {menuActions.length > 0 && (
                                          <Menu as="div" className="relative h-8 w-8">
                                            <Menu.Button className="w-full h-full flex items-center justify-center rounded-md bg-[#F1F1F1] hover:bg-gray-200 transition-colors">
                                              <FiMoreVertical className="w-5 h-5 text-gray-600" />
                                            </Menu.Button>
                                            <Transition
                                              as={Fragment}
                                              enter="transition ease-out duration-100"
                                              enterFrom="transform opacity-0 scale-95"
                                              enterTo="transform opacity-100 scale-100"
                                              leave="transition ease-in duration-75"
                                              leaveFrom="transform opacity-100 scale-100"
                                              leaveTo="transform opacity-0 scale-95"
                                            >
                                              <Menu.Items className="absolute right-12 top-1/2 -translate-y-1/2 w-48 origin-top-right bg-white shadow-xl border border-gray-100 rounded-lg z-[80] focus:outline-none">
                                                <div className="py-1">
                                                  {menuActions.map((action) => {
                                                    const menuItemNode = (
                                                      <Menu.Item key={action.key}>
                                                        {({ active }) => renderActionButton(action, true, false, active)}
                                                      </Menu.Item>
                                                    );
                                                    if (action.rbacActionId) {
                                                      return (
                                                        <RBACFlag actionId={action.rbacActionId} key={action.key}>
                                                          {menuItemNode}
                                                        </RBACFlag>
                                                      );
                                                    }
                                                    return menuItemNode;
                                                  })}
                                                </div>
                                              </Menu.Items>
                                            </Transition>
                                          </Menu>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                              </tr>
                            );
                          })}
                          {open && <SendWhatsappScriptimport open={open} setOpen={setOpen} bookingId={bookingId} />}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full min-w-full h-full flex items-center justify-center bg-white rounded-lg">
                <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Appointments" />
              </div>
            )}
          </div>
        </div>
      </div>
      {/* )} */}
      <div className="text-left pl-2 pt-2 text-gray-600 text-xs">
        Total Bookings <strong>{customerBookings?.count}</strong>{" "}
      </div>
    </div>
  );
};

export default AppointmentTableComponent;
